import type { Map, System, Layouts, IndoorUnit, OutdoorUnit, Accessory } from '@coldtec/cos-types';

/**
 *  Available store action for the systems.
 */
export enum SystemsAction {
    /** Used to fetch the available filters. */
    FILTERS_FETCH = 'systems:filters:fetch',

    /** Used to set the filters. */
    FILTERS_SET = 'systems:filters:set',

    /** Used to fetch the suggestions, based on the configured spaces. */
    FETCH_SUGGESTIONS = 'sytems:suggestions:fetch',

    /** Used to show the suggestions for a specific space. */
    SHOW_FOR_SPACE = 'systems:showForSpace',

    /** Used to select a system for the currently showing space(s). */
    SELECT = 'systems:select',

    /** Used to sync the suggestions (tool systems) with the request data. */
    SYNC_SUGGESTIONS = 'systems:sync:suggestions',
}

/**
 *  Available store getter for the systems.
 */
export enum SystemsGetter {
    /** Used to get the available filters. */
    FILTERS_AVAILABLE = 'systems:filters:available',

    /** Used to get the state of the filter(s). */
    FILTER_STATE = 'systems:filter:state',

    /** Used to get the general active state of the filter(s). */
    FILTER_ACTIVE = 'systems:filter:active',

    /** Used to get all the single split system suggestions. */
    SUGGESTIONS_SINGLE = 'systems:suggestions:single',

    /** Used to get all the multi split system suggestions. */
    SUGGESTIONS_MULTI = 'systems:suggestions:multi',

    /** Used to get all the system suggestions. */
    SUGGESTIONS_ALL = 'systems:suggestions:all',

    /** Used to get all the selected system suggestions. */
    SUGGESTIONS_SELECTED = 'systems:suggestions:selected',

    /** Used to get all the system suggestions of the currently showing space. */
    SUGGESTIONS_OF_SHOWING_SPACE = 'systems:suggestions:ofShowingSpace',

    /** Used to get all the (data of the) systems that are suggested. */
    SUGGESTIONS_SYSTEMS = 'systems:suggestions:systems',

    /** Used to get the (tool) space(s) for which the suggestions are currently shown. */
    SPACES = 'systems:spaces',
}

/**
 *  Available store mutation for the systems.
 */
export enum SystemsMutation {
    /** Used to update the loading state of the suggestions. */
    LOADING = 'systems:loading',

    /** Used to store the available filters and construct their initial state. */
    FILTERS_AVAILABLE = 'sytems:filters:available',

    /** Used to update the state of the filters. */
    FILTERS_STATE = 'systems:filters:state',

    /**
     *  Used to store the fetched system suggestions and
     *  set the system's loading state to `false`.
     */
    SUGGESTIONS = 'sytems:suggestions',

    /**
     *
     *  Used to store the systemSpaceId of the space for
     *  which the suggestions should be shown.
     *
     *  (i) The `SystmsSuggestions.vue` component on
     *  his turn renders the correct systems
     *
     */
    SUGGESTIONS_SHOW_FOR_SPACE = 'systems:suggestions:showForSpace',
}

/**
 *  Holding the label and value of a filter option.
 */
export interface SystemsFilterOption {

    /**
     *  (Checkbox) label of the filter option.
     */
    label: string;

    /**
     *  Value of the filter.
     */
    value: string | number;

}

/**
 *
 *  Holding the details of a filter group of the systems.
 *
 *  (i) A filter group is represented by a group of checkboxes with a title.
 *
 */
export interface SystemsFilterGroup {

    /**
     *  Column on which the filter applies.
     */
    column: string;

    /**
     *  Title of the filters groep.
     */
    title: string;

    /**
     *  Available filter options.
     */
    options: Array<SystemsFilterOption>;

}

/**
 *  Holding the active state and values to filter
 *  on per column for the system filters.
 */
export interface SystemsFilters {
    [column: string]: {
        [value: string]: boolean;
    };
}

/**
 *  Available location of a system's unit.
 */
export enum SystemUnitLocation {
    Outdoor = 'outdoor',
    Indoor = 'indoor',
}

/**
 *  Available type of (tool) system.
 */
export enum SystemSplit {
    Single = 'single',
    Multi = 'multi',
}

/**
 *  Holding the details of a tool unit, including the guid
 *  of the unit, his local id, the tool space id for which
 *  it got selected, the tool space id of the space
 *  for which is was selected, the selected color and
 *  the original data.
 */
export interface ToolUnit {
    /** Holding the guid of the unit. */
    guid: string;

    /** Holding the location of the unit. */
    location: SystemUnitLocation;

    /**
     *
     *  Holding the local id of the tool unit.
     *
     *  (i) Used to identify the exact same unit
     *  as two separate ones. Because a unit can
     *  be part of a multi split several times,
     *  checking on guid doesn't work.
     *
     */
    localId: string;

    /** Holding the tool space id of the space for which this unit is selected. */
    toolSpaceId: string | null;

    /** Holding the tool space id of the space for which this unit was selected. */
    resetToolSpaceId: string | null;

    /**
     *
     *  Holding the guid of the selected color for this unit.
     *
     *  (i) Bound to this object, instead of the space's object,
     *  because we want to allow users to browse the colors
     *  before selecting a space.
     *
     */
    color: string | null;

    /** Holding actual data of the unit. */
    data: IndoorUnit | OutdoorUnit;
}

/**
 *
 *  Holding the details of a tool system, including
 *  the type, guid of the system, his selected state
 *  and optional layout (in case of a multi split).
 *
 *  (i) The selected state is relative to the
 *  space(s) to which it belongs
 *
 */
export interface ToolSystem {
    /** The type of split. */
    split: SystemSplit;

    /** The guid of the system. */
    guid: string;

    /** The selected state of this system. */
    selected: boolean;

    /** The indoor (tool) units that are part of the system. */
    indoorUnits: Array<ToolUnit>;

    /** The outdoor (tool) units that are part of the system. */
    outdoorUnits: Array<ToolUnit>;

    /** The (tool) accessories that are part of the system. */
    accessories: Array<ToolAccessory>;

    /** The possible indoor units layouts (in case of a multi split). */
    layouts?: Layouts;
}

/**
 *  Holding the details of a tool accessory, including
 *  the guid of the accessory, the selected state and
 *  the data of the accessory.
 */
export interface ToolAccessory {
    /** The guid of the accessory. */
    guid: string;

    /** The selected state of the accessory. */
    selected: boolean;

    /** The actual data of the accessory. */
    data: Accessory;
}

/**
 *  Holding the system suggestion for the spaces whose
 *  toolSpaceIds are present in the `toolSpaceIds` property.
 */
export interface ToolSystemSuggestion {
    /**
     *
     *  Holding the tool space ids of the spaces for which these system suggestions are.
     *
     *  (i) Can be either one (for a single split) or multi (for a multi split)
     *
     */
    toolSpaceIds: Array<string>;

    /** Holding the system's suggestions tool systems. */
    systems: Array<ToolSystem>;
}

/**
 *  Holding the selected (tool) system for a space.
 */
export interface SelectedToolSystems {
    [toolSpaceId: string]: ToolSystem;
}

/**
 *
 *  Holding a map of the system suggestions per space
 *  (single), or combination of spaces (multi).
 *
 *  (i) The key (systemSpaceId) is composed from the
 *  `toolSpaceIds` of the spaces for which the system
 *  suggestion is, separated by '-'.
 *
 */
export interface ToolSystemSuggestionMap {
    [systemSpaceId: string]: ToolSystemSuggestion;
}

/**
 *  Holding the system suggestions.
 */
export interface SystemsSuggestions {
    /** Holding all the single split suggestions. */
    single: ToolSystemSuggestionMap;

    /** Holding all the multi split suggestions. */
    multi: ToolSystemSuggestionMap;

    /** Holding the systemSpaceId of the space whose system suggestions are shown. */
    show: string | null;

    /** Holding all the systems that are suggested. */
    systems: Map<System>;
}

/**
 *  Holding the store's systems state.
 */
export interface SystemsState {
    /**
     *  Holding the loading state of the systems, either fetching
     *  filters, systems or loading filters for a specific space.
     */
    loading: boolean;

    /**
     *  Holding the available filters and the filter state.
     */
    filters: {
        /** Holding the available filters. */
        available: Array<SystemsFilterGroup>;

        /** Holding the current state of the filters. */
        state: SystemsFilters;
    };

    /**
     *  Holding the system suggestions.
     */
    suggestions: SystemsSuggestions;
}
